/**
 * "A new version is available": notice when the deployed app has moved on.
 *
 * A browser tab that was opened before a deploy keeps running the old bundle
 * indefinitely. The static assets are content-hashed and served `immutable`, so
 * the old tab never fetches the new ones, and the person carries on using a
 * build that may be hours or days old. That is how a fixed bug appears not to be
 * fixed, and how a tester re-tests a stale screen.
 *
 * How it notices. nginx already serves index.html with
 * `no-cache, no-store, must-revalidate` (see client/nginx.conf), so re-fetching
 * it always reaches the origin. Create React App stamps a content hash into the
 * main bundle filename, so that name IS the version identity: if the shell now
 * points at a different main.<hash>.js than the one this tab booted with, a new
 * version is deployed. No version endpoint, no build-time injection and no
 * service worker are needed. Borrowed from the same approach in DOCUMED.
 *
 * What it deliberately does NOT do: reload by itself. Reloading someone's tab
 * without asking would interrupt whatever they are in the middle of. The hook
 * only reports; the banner asks; the person decides.
 *
 * And it stops entirely during a video call — see `enabled` below. A reload
 * drops a live call, so a client or therapist mid-session is never invited to
 * do that, and we do not even poll while they are in one.
 */
import { useEffect, useState } from 'react';

/** How often to look, once the first check has run. */
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Wait before the first check so it never competes with initial page load. */
export const FIRST_CHECK_DELAY_MS = 60 * 1000;

// CRA emits build/static/js/main.<contenthash>.js. Matching the whole filename
// (not just the hash) keeps this readable in logs and in test failures.
const BUNDLE_RE = /static\/js\/(main\.[A-Za-z0-9_-]+)\.js/;

/**
 * The main bundle filename referenced by a served index.html.
 * @returns {string|null} e.g. "main.b7a30b57" or null if the shell looks unfamiliar
 */
export function extractBundleName(html) {
  const m = String(html || '').match(BUNDLE_RE);
  return m ? m[1] : null;
}

/**
 * The main bundle filename THIS tab actually loaded, read from its own script
 * tags. Avoids needing the build to inject a version.
 * @returns {string|null} null in dev, where CRA serves an unhashed bundle
 */
export function loadedBundleName(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return null;
  const scripts = Array.from(doc.querySelectorAll('script[src]'));
  for (const s of scripts) {
    const name = extractBundleName(s.getAttribute('src') || '');
    if (name) return name;
  }
  return null;
}

/**
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - pass false to suspend entirely (during a call)
 * @returns {boolean} true once the deployed shell references a different bundle
 */
export default function useAppUpdateAvailable({ enabled = true } = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!enabled || updateAvailable) return undefined;

    // No hashed bundle means local development, where this would only ever be
    // noise. Nothing to compare against, so do nothing at all.
    const mine = loadedBundleName();
    if (!mine) return undefined;

    let stopped = false;

    const check = async () => {
      try {
        // Cache-busting query AND no-store: belt and braces, because a proxy in
        // front of us is not ours to assume anything about.
        const res = await fetch(`/?updateCheck=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const deployed = extractBundleName(await res.text());
        if (!stopped && deployed && deployed !== mine) setUpdateAvailable(true);
      } catch (err) {
        // Offline, or the origin is briefly unreachable mid-deploy. Try later;
        // never surface this to the person using the app.
      }
    };

    const firstCheck = window.setTimeout(check, FIRST_CHECK_DELAY_MS);
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    // Coming back to a tab left open overnight is the likeliest moment to be
    // running something stale, and the cheapest moment to find out.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      window.clearTimeout(firstCheck);
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, updateAvailable]);

  return updateAvailable;
}
